import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  Image as ImageIcon, 
  Heart, 
  MessageCircle, 
  Send, 
  Upload,
  X,
  MoreVertical,
  Trash2,
  Building2,
  Camera,
  Plus
} from 'lucide-react';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Feed() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newPost, setNewPost] = useState({ content: '', image_url: '', store_id: '' });
  const [uploadingImage, setUploadingImage] = useState(false);
  const [commentText, setCommentText] = useState({});
  const [selectedStore, setSelectedStore] = useState('all');
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, [selectedStore]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      const [postsRes, storesRes] = await Promise.all([
        axios.get(`${API_URL}/feed/posts`, {
          params: selectedStore !== 'all' ? { store_id: selectedStore } : {}
        }),
        axios.get(`${API_URL}/stores`)
      ]);
      
      setPosts(postsRes.data);
      setStores(storesRes.data);
    } catch (error) {
      console.error('Error fetching feed:', error);
      toast.error('Failed to load feed');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setUploadingImage(true);
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API_URL}/upload`, formData, {
        timeout: 60000
      });

      setNewPost(prev => ({ ...prev, image_url: response.data.url }));
      toast.success('Image uploaded');
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const createPost = async () => {
    if (!newPost.content.trim()) {
      toast.error('Please write something');
      return;
    }

    try {
      const response = await axios.post(`${API_URL}/feed/posts`, {
        content: newPost.content,
        image_url: newPost.image_url || null,
        store_id: newPost.store_id || null
      });

      setPosts([response.data, ...posts]);
      setNewPost({ content: '', image_url: '', store_id: '' });
      setCreateDialogOpen(false);
      toast.success('Post created!');
    } catch (error) {
      console.error('Error creating post:', error);
      toast.error('Failed to create post');
    }
  };

  const deletePost = async (postId) => {
    try {
      await axios.delete(`${API_URL}/feed/posts/${postId}`);
      setPosts(posts.filter(p => p.id !== postId));
      toast.success('Post deleted');
    } catch (error) {
      console.error('Error deleting post:', error);
      toast.error('Failed to delete post');
    }
  };

  const toggleLike = async (postId) => {
    try {
      const response = await axios.post(`${API_URL}/feed/posts/${postId}/like`);
      
      setPosts(posts.map(post => {
        if (post.id === postId) {
          const likes = response.data.action === 'liked'
            ? [...post.likes, user.id]
            : post.likes.filter(id => id !== user.id);
          return { ...post, likes };
        }
        return post;
      }));
    } catch (error) {
      console.error('Error toggling like:', error);
    }
  };

  const addComment = async (postId) => {
    const content = commentText[postId]?.trim();
    if (!content) return;

    try {
      const response = await axios.post(`${API_URL}/feed/posts/${postId}/comment`, {
        content
      });

      setPosts(posts.map(post => {
        if (post.id === postId) {
          return { ...post, comments: [...post.comments, response.data.comment] };
        }
        return post;
      }));
      
      setCommentText(prev => ({ ...prev, [postId]: '' }));
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error('Failed to add comment');
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
    
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div className="space-y-6" data-testid="feed-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Team Feed
          </h1>
          <p className="text-slate-600 mt-1">
            Share updates, photos, and stories from your A&W locations
          </p>
        </div>
        
        <Button
          onClick={() => setCreateDialogOpen(true)}
          className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm"
          data-testid="create-post-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Post
        </Button>
      </div>

      {/* Store Filter */}
      {user?.role === 'owner' && stores.length > 0 && (
        <div className="flex gap-4">
          <Select value={selectedStore} onValueChange={setSelectedStore}>
            <SelectTrigger className="w-48 rounded-sm border-slate-200" data-testid="store-filter">
              <Building2 className="w-4 h-4 mr-2 text-slate-400" />
              <SelectValue placeholder="All Stores" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Stores</SelectItem>
              {stores.map((store) => (
                <SelectItem key={store.id} value={store.id}>
                  {store.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Create Post Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>Create Post</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Textarea
              value={newPost.content}
              onChange={(e) => setNewPost(prev => ({ ...prev, content: e.target.value }))}
              placeholder="Share what's happening at your store..."
              className="min-h-[120px] rounded-sm"
              data-testid="post-content-input"
            />

            {newPost.image_url && (
              <div className="relative">
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}${newPost.image_url}`}
                  alt="Upload preview"
                  className="w-full h-48 object-cover rounded-sm"
                />
                <Button
                  variant="destructive"
                  size="icon"
                  className="absolute top-2 right-2 h-8 w-8"
                  onClick={() => setNewPost(prev => ({ ...prev, image_url: '' }))}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            )}

            <div className="flex items-center gap-4">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleImageUpload}
              />
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingImage}
                className="rounded-sm"
                data-testid="upload-image-btn"
              >
                <Camera className="w-4 h-4 mr-2" />
                {uploadingImage ? 'Uploading...' : 'Add Photo'}
              </Button>

              {user?.role === 'owner' && stores.length > 0 && (
                <Select
                  value={newPost.store_id}
                  onValueChange={(value) => setNewPost(prev => ({ ...prev, store_id: value }))}
                >
                  <SelectTrigger className="w-40 rounded-sm border-slate-200">
                    <SelectValue placeholder="Tag store" />
                  </SelectTrigger>
                  <SelectContent>
                    {stores.map((store) => (
                      <SelectItem key={store.id} value={store.id}>
                        {store.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button
                variant="outline"
                onClick={() => setCreateDialogOpen(false)}
                className="rounded-sm"
              >
                Cancel
              </Button>
              <Button
                onClick={createPost}
                className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm"
                data-testid="submit-post-btn"
              >
                Post
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Posts Feed */}
      {loading ? (
        <div className="text-center py-12 text-slate-500" data-testid="loading">
          Loading feed...
        </div>
      ) : posts.length === 0 ? (
        <Card className="bg-white border border-slate-200 rounded-sm" data-testid="empty-state">
          <CardContent className="py-12 text-center">
            <ImageIcon className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-bold text-slate-900 mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
              No posts yet
            </h3>
            <p className="text-slate-600 mb-6">
              Be the first to share something with your team!
            </p>
            <Button
              onClick={() => setCreateDialogOpen(true)}
              className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create First Post
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4 max-w-2xl" data-testid="posts-feed">
          {posts.map((post) => (
            <Card key={post.id} className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid={`post-${post.id}`}>
              <CardContent className="p-0">
                {/* Post Header */}
                <div className="flex items-center justify-between p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-slate-900 rounded-full flex items-center justify-center text-white font-medium">
                      {post.author_name?.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{post.author_name}</p>
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        {post.store_name && (
                          <>
                            <Building2 className="w-3 h-3" />
                            <span>{post.store_name}</span>
                            <span>•</span>
                          </>
                        )}
                        <span>{formatDate(post.created_at)}</span>
                      </div>
                    </div>
                  </div>

                  {(post.author_id === user?.id || user?.role === 'owner') && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() => deletePost(post.id)}
                          className="text-red-600"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>

                {/* Post Content */}
                <div className="px-4 pb-3">
                  <p className="text-slate-700 whitespace-pre-wrap">{post.content}</p>
                </div>

                {/* Post Image */}
                {post.image_url && (
                  <div className="px-4 pb-4">
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}${post.image_url}`}
                      alt="Post"
                      className="w-full rounded-sm object-cover max-h-96"
                    />
                  </div>
                )}

                {/* Actions */}
                <div className="px-4 py-3 border-t border-slate-100 flex items-center gap-4">
                  <button
                    onClick={() => toggleLike(post.id)}
                    className={`flex items-center gap-1.5 text-sm ${
                      post.likes?.includes(user?.id) ? 'text-red-500' : 'text-slate-500 hover:text-red-500'
                    }`}
                    data-testid={`like-btn-${post.id}`}
                  >
                    <Heart className={`w-5 h-5 ${post.likes?.includes(user?.id) ? 'fill-current' : ''}`} />
                    <span>{post.likes?.length || 0}</span>
                  </button>
                  <button className="flex items-center gap-1.5 text-sm text-slate-500">
                    <MessageCircle className="w-5 h-5" />
                    <span>{post.comments?.length || 0}</span>
                  </button>
                </div>

                {/* Comments Section */}
                {post.comments?.length > 0 && (
                  <div className="px-4 pb-3 space-y-3">
                    {post.comments.slice(-3).map((comment) => (
                      <div key={comment.id} className="flex gap-2">
                        <div className="w-7 h-7 bg-slate-200 rounded-full flex items-center justify-center text-slate-600 text-xs font-medium flex-shrink-0">
                          {comment.author_name?.charAt(0).toUpperCase()}
                        </div>
                        <div className="bg-slate-50 rounded-sm px-3 py-2 flex-1">
                          <p className="text-xs font-medium text-slate-900">{comment.author_name}</p>
                          <p className="text-sm text-slate-700">{comment.content}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add Comment */}
                <div className="px-4 pb-4 flex gap-2">
                  <input
                    type="text"
                    value={commentText[post.id] || ''}
                    onChange={(e) => setCommentText(prev => ({ ...prev, [post.id]: e.target.value }))}
                    placeholder="Write a comment..."
                    className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                    onKeyPress={(e) => e.key === 'Enter' && addComment(post.id)}
                    data-testid={`comment-input-${post.id}`}
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => addComment(post.id)}
                    disabled={!commentText[post.id]?.trim()}
                    className="h-9 w-9"
                    data-testid={`send-comment-btn-${post.id}`}
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
